import { FC, useState } from 'react';
import { AreaIcon24 } from '@components/ui/Icons';
import { ArrowBack, CalendarMonth, ViewList, Add } from '@mui/icons-material';
import { FileText, Download, Sparkles } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { Button } from '@components/atoms/Button';
import { useCanWrite } from '@hooks/useCanWrite';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';

interface NavigationProps {
  projectId: string;
  timelineRefs: React.MutableRefObject<{ [key: string]: any }>;
  toggleNewTaskModal: (idProject: string) => void;
  toggleNewEventModal: (idProject: string) => void;
  toggleCreateTemplateModal?: (idProject: string) => void;
  toggleImportTemplateModal?: (idProject: string) => void;
  isDelegated: boolean;
  userRole?: string | null;
  /**
   * Sprint 31, story-161. Fetched by the caller (not here) so this leaf component's existing
   * tests — which render it standalone, with no `QueryClientProvider` in the tree — keep
   * working unchanged, matching `userRole`'s own caller-supplied-prop pattern above.
   */
  aiPlanningAvailable?: boolean;
  /**
   * Sprint 38, story-209. Translated labels for the Project/Month switch, supplied by the caller
   * for the same reason as `aiPlanningAvailable` (this leaf's own tests render it with no Redux
   * `<Provider>`, which `useTranslation` requires). English defaults keep those tests unchanged.
   */
  /** Translated Project/Month labels; the caller (Accordion) owns the translations. */
  viewLabels: {
    project: string;
    month: string;
    monthTooltip: string;
    group: string;
  };
}

export const Navigation: FC<NavigationProps> = ({
  projectId,
  timelineRefs,
  toggleNewTaskModal,
  toggleNewEventModal,
  toggleCreateTemplateModal,
  toggleImportTemplateModal,
  isDelegated,
  userRole,
  aiPlanningAvailable,
  viewLabels,
}) => {
  const history = useHistory();
  const { canWrite } = useCanWrite();
  const canManageTemplates = roleRank(userRole) >= TEMPLATE_ACTIONS_MIN_RANK;
  // `project` is the default: the schedule opens on the whole-project view (story-209).
  const [selectedView, setSelectedView] = useState<
    'project' | 'month' | 'calendar' | 'taskList'
  >('project');

  const handleProjectClick = () => {
    setSelectedView('project');
    const timelineRef = timelineRefs.current[projectId];
    if (timelineRef && timelineRef.toggleProject) {
      timelineRef.toggleProject();
    }
  };

  const handleMonthClick = () => {
    setSelectedView('month');
    const timelineRef = timelineRefs.current[projectId];
    if (timelineRef && timelineRef.toggleMonth) {
      timelineRef.toggleMonth();
    }
  };

  const handleCalendarClick = () => {
    setSelectedView('calendar');
    const timelineRef = timelineRefs.current[projectId];
    if (timelineRef && timelineRef.toggleView) {
      timelineRef.toggleView();
    }
  };

  const handleTaskListClick = () => {
    setSelectedView('taskList');
    const timelineRef = timelineRefs.current[projectId];
    if (timelineRef && timelineRef.toggleTaskList) {
      timelineRef.toggleTaskList();
    }
  };

  const handleBack = () => {
    history.push(`/projects/${projectId}`);
  };

  const handleAddTask = () => {
    toggleNewTaskModal(projectId);
  };

  const handleAddEvent = () => {
    toggleNewEventModal(projectId);
  };

  const handleCreateTemplate = () => {
    toggleCreateTemplateModal?.(projectId);
  };

  const handleImportTemplate = () => {
    toggleImportTemplateModal?.(projectId);
  };

  const handleGenerateWithAi = () => {
    history.push(`/projects/${projectId}/plan/ai`);
  };

  return (
    <div className="bg-white border-b border-gray-200 p-4">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleBack}
            leftIcon={<ArrowBack className="w-4 h-4" />}>
            Go to Project
          </Button>

          <div
            role="group"
            aria-label={viewLabels.group}
            className="inline-flex items-center gap-0">
            <Button
              isSelected={selectedView === 'project'}
              aria-pressed={selectedView === 'project'}
              variant="tertiary"
              onClick={handleProjectClick}
              leftIcon={<AreaIcon24 className="w-4 h-4" />}>
              {viewLabels.project}
            </Button>

            <Button
              isSelected={selectedView === 'month'}
              aria-pressed={selectedView === 'month'}
              title={viewLabels.monthTooltip}
              variant="tertiary"
              onClick={handleMonthClick}>
              {viewLabels.month}
            </Button>
          </div>

          <Button
            isSelected={selectedView === 'calendar'}
            variant="tertiary"
            onClick={handleCalendarClick}
            leftIcon={<CalendarMonth className="w-4 h-4" />}>
            Monthly View
          </Button>
          <Button
            isSelected={selectedView === 'taskList'}
            variant="tertiary"
            onClick={handleTaskListClick}
            leftIcon={<ViewList className="w-4 h-4" />}>
            View Task List
          </Button>
        </div>

        <div className="flex gap-2">
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleAddTask}
              leftIcon={<Add className="w-4 h-4" />}>
              Add Task
            </Button>

            {!isDelegated && (
              <Button
                variant="primary"
                onClick={handleAddEvent}
                leftIcon={<Add className="w-4 h-4" />}>
                Add Event
              </Button>
            )}
            {!isDelegated && canWrite && canManageTemplates && toggleCreateTemplateModal && (
              <Button
                variant="secondary"
                onClick={handleCreateTemplate}
                leftIcon={<FileText className="w-4 h-4" />}>
                Export Tasks
              </Button>
            )}
            {!isDelegated && canWrite && canManageTemplates && toggleImportTemplateModal && (
              <Button
                variant="secondary"
                onClick={handleImportTemplate}
                leftIcon={<Download className="w-4 h-4" />}>
                Import Tasks
              </Button>
            )}
            {!isDelegated && canWrite && canManageTemplates && aiPlanningAvailable && (
              <Button
                variant="secondary"
                onClick={handleGenerateWithAi}
                leftIcon={<Sparkles className="w-4 h-4" />}
                data-testid="schedule-generate-with-ai">
                {/* This whole file predates the i18n migration (every sibling label here is
                    hardcoded English too — `useTranslation` needs a Redux `<Provider>` this
                    component's own standalone tests don't wrap); `schedule.generateWithAi` is
                    defined in the locale catalogs, ready for this file's eventual migration. */}
                Generate with AI
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
